import { useEffect, useState } from "react";
import { Asset, Referral, Report, SeizureRestraintRecord } from "types";
import Accordion from "../../../components/accordion/Accordion";
import AssetDetail from "./AssetDetail";
import FileViewer from "components/file-components/fileViewer2";
import { fetchAssetReferralById } from "services/assetRecoveryService";
import { AssetGovernancePopup } from "./AddAssetGovernance";
import SeizureRestraintForm from "./AddProvision";

export default function AssetList({
  referralId,
  referral,
  onAssetCreated,
  setActiveTab,
  reports,
  recoveryReports,
  onAddGovernance,
  onEditGovernance,
  onAddProvision,
  handleOpenAssetDetail,
}: {
  referralId: string | null;
  referral: Referral | null;
  onAssetCreated?: (data: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  reports?: Report[];
  recoveryReports?: Report[];
  onAddGovernance?: (asset: Asset) => void;
  onEditGovernance?: (asset: Asset) => void;
  onAddProvision?: (asset: Asset) => void;
  handleOpenAssetDetail?: (asset: Asset) => void;
}) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  // State for governance popup
  const [isGovernancePopup, setIsGovernancePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAssetForGovernance, setSelectedAssetForGovernance] =
    useState<Asset | null>(null);

  // State for provision popup
  const [isProvisionPopupOpen, setIsProvisionPopupOpen] = useState(false);
  const [selectedAssetForProvision, setSelectedAssetForProvision] =
    useState<Asset | null>(null);

  useEffect(() => {
    if (referral) {
      setAssets(referral.assets || []);
    }
  }, [referral]);

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  const handleAddGovernance = (asset: Asset) => {
    setSelectedAssetForGovernance(asset);
    setIsEditMode(false);
    setIsGovernancePopup(true);
  };

  const handleEditGovernance = (asset: Asset) => {
    setSelectedAssetForGovernance(asset);
    setIsEditMode(true);
    setIsGovernancePopup(true);
  };

  const handleAddProvision = (asset: Asset) => {
    setSelectedAssetForProvision(asset);
    setIsProvisionPopupOpen(true);
  };

  const handleCloseGovernancePopup = () => {
    setIsGovernancePopup(false);
    setSelectedAssetForGovernance(null);
    setIsEditMode(false);
  };

  const handleCloseProvisionPopup = () => {
    setIsProvisionPopupOpen(false);
    setSelectedAssetForProvision(null);
  };

  const handlePopupSuccess = async () => {
    const response = await fetchAssetReferralById(referralId as string);
    if (onAssetCreated && response) {
      onAssetCreated(response);
      setActiveTab?.("Assets");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-2">
        {assets.map((asset) => (
          <div
            key={asset.id}
            className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900">
              {asset.asset_unique_id || "N/A"}
            </span>

            <button
              onClick={() =>
                handleOpenAssetDetail && handleOpenAssetDetail(asset)
              }
              className="inline-flex items-center px-3 py-1.5 bg-[#212121] hover:bg-[#414040] text-white text-xs font-medium rounded-md transition-colors"
            >
              View Details
            </button>
          </div>
        ))}

        {/* Empty state */}
        {assets.length === 0 && (
          <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
            <p className="text-gray-500 text-sm">No assets found</p>
          </div>
        )}
      </div>

      {assets.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-600">No assets found.</p>
        </div>
      )}

      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}
